import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Power,
  PowerOff,
  FilePlus,
  ChevronDown,
  ChevronRight,
  Upload,
  Download,
  FolderUp,
  X,
  Loader2,
  Info,
} from 'lucide-react';
import { LISTENER_SETUP_HELP } from '../listener_setup_help.jsx';
import { createPortal } from 'react-dom';
import {
  getAgentSkill,
  readAgentSkillFile,
  writeAgentSkillFile,
  deleteAgentSkillFile,
  runAgentSkillCommand,
  getAgentSkillCredentials,
  putAgentSkillCredentials,
  getAgentSkillCredentialSuggestions,
  copyAgentSkillCredentialsFrom,
  uninstallAgentSkill,
  enableAgentSkill,
  disableAgentSkill,
  fetchAgentSkillRuns,
  clearAgentSkillRuns,
  downloadAgentSkillZip,
  uploadAgentSkillFolder,
} from '../data';
import SkillFileTree from '../components/SkillFileTree';
import { BROWSER_TZ } from '../components/SchedulePanel';
import './SkillWorkspace.css';

const SENSITIVE_RE = /secret|token|password|key|credential/i;
const HISTORY_LIMIT = 100;

function languageForPath(path) {
  if (!path) return 'plaintext';
  const lower = path.toLowerCase();
  if (lower.endsWith('.py')) return 'python';
  if (lower.endsWith('.md')) return 'markdown';
  if (lower.endsWith('.json')) return 'json';
  if (lower.endsWith('.js') || lower.endsWith('.jsx')) return 'javascript';
  if (lower.endsWith('.ts') || lower.endsWith('.tsx')) return 'typescript';
  if (lower.endsWith('.sh') || lower.endsWith('.bash')) return 'shell';
  if (lower.endsWith('.yaml') || lower.endsWith('.yml')) return 'yaml';
  if (lower.endsWith('.toml')) return 'plaintext';
  if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'html';
  if (lower.endsWith('.css')) return 'css';
  return 'plaintext';
}

function pickInitialFile(files) {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) return '';
  const doc = list.find((f) => f === 'description.md') || list.find((f) => f === 'SKILL.md');
  if (doc) return doc;
  const data = list.find((f) => f === 'data.json');
  if (data) return data;
  const main = list.find((f) => f === 'main.py' || f === 'index.js' || f === 'main.js');
  if (main) return main;
  return list[0];
}

const PROTECTED_SKILL_FILES = new Set(['description.md', 'data.json', 'main.py', 'SKILL.md']);

function historyKey(agentId, skillId) {
  return `aegent:skill-runs:${agentId}:${skillId}`;
}

function loadHistory(agentId, skillId) {
  try {
    const raw = localStorage.getItem(historyKey(agentId, skillId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(agentId, skillId, entries) {
  try {
    localStorage.setItem(historyKey(agentId, skillId), JSON.stringify(entries.slice(0, HISTORY_LIMIT)));
  } catch {
    /* localStorage full — silently drop */
  }
}

function CredentialsPanel({ agentId, skill }) {
  const [meta, setMeta] = useState({ keys: [], credentials: {} });
  const [values, setValues] = useState({});
  const [revealed, setRevealed] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [removed, setRemoved] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  // {KEY: {fromSlug, fromName}} for keys this skill is missing but a sibling
  // skill already has stored. Backed by /credential-suggestions; the actual
  // values stay server-side until the user accepts a copy.
  const [suggestions, setSuggestions] = useState({});
  const [copying, setCopying] = useState('');

  const help = LISTENER_SETUP_HELP[skill?.id];

  const declared = useMemo(() => {
    const fm = skill?.frontmatter || {};
    const mf = skill?.manifest || {};
    // data.json declares credentials as objects ({ name, ... }); legacy
    // frontmatter declares them as plain strings. Normalize both to key names.
    const names = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((c) => (typeof c === 'string' ? c : c && c.name ? String(c.name) : null))
        .filter(Boolean);
    return Array.from(new Set([...names(fm.credentials), ...names(mf.credentials)]));
  }, [skill]);

  const refetch = useCallback(async () => {
    const data = await getAgentSkillCredentials(agentId, skill.id);
    setMeta(data || { keys: [], credentials: {} });
    const next = {};
    const savedKeys = new Set(data?.keys || []);
    (data?.keys || []).forEach((k) => { next[k] = ''; });
    // Auto-detect timezone for any declared *_TIMEZONE field that isn't already
    // saved -- mirrors the cron schedule UI's BROWSER_TZ default so users
    // don't have to type "America/Chicago" themselves. They can still edit
    // before saving.
    (declared || []).forEach((k) => {
      if (/_TIMEZONE$/i.test(k) && !savedKeys.has(k)) {
        next[k] = BROWSER_TZ;
      }
    });
    // Fetched separately so an outage on one endpoint doesn't blank the other.
    try {
      const s = await getAgentSkillCredentialSuggestions(agentId, skill.id);
      setSuggestions((s && s.suggestions) || {});
    } catch {
      setSuggestions({});
    }
  }, [agentId, skill?.id, declared]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        await refetch();
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load credentials');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refetch]);

  // Group suggestions by source skill so we render one banner per sibling
  // ("Use SMTP_* from Send Email") instead of one per individual key.
  const suggestionGroups = useMemo(() => {
    const by = new Map();
    for (const [key, src] of Object.entries(suggestions || {})) {
      if (!src || !src.fromSlug) continue;
      if (!by.has(src.fromSlug)) by.set(src.fromSlug, { name: src.fromName || src.fromSlug, keys: [] });
      by.get(src.fromSlug).keys.push(key);
    }
    return Array.from(by.entries()).map(([slug, v]) => ({ slug, ...v }));
  }, [suggestions]);

  const handleCopyFrom = async (fromSlug, keys) => {
    setCopying(fromSlug);
    setError('');
    try {
      await copyAgentSkillCredentialsFrom(agentId, skill.id, fromSlug, keys);
      await refetch();
    } catch (e) {
      setError(e.message || 'Failed to copy credentials');
    } finally {
      setCopying('');
    }
  };

  const allKeys = useMemo(() => {
    const set = new Set([...(meta.keys || []), ...declared, ...Object.keys(values)]);
    return Array.from(set);
  }, [meta.keys, declared, values]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      const toRemove = removed.filter((k) => !(k in payload));
      if (Object.keys(payload).length === 0 && toRemove.length === 0) {
        setError('No changes to save. Type in fields you want to update.');
        setSaving(false);
        return;
      }
      await putAgentSkillCredentials(agentId, skill.id, { credentials: payload, remove: toRemove });
      const refreshed = await getAgentSkillCredentials(agentId, skill.id);
      setMeta(refreshed || { keys: [], credentials: {} });
      const next = {};
      (refreshed?.keys || []).forEach((k) => { next[k] = ''; });
      setValues(next);
      setRevealed({});
      setRemoved([]);
    } catch (e) {
      setError(e.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPair = () => {
    if (!newKey.trim()) return;
    setValues((prev) => ({ ...prev, [newKey.trim()]: newValue }));
    setNewKey('');
    setNewValue('');
  };

  const handleRemove = (k) => {
    if (!window.confirm(`Remove credential "${k}"? This will not persist until you save.`)) return;
    setValues((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    // Only stored keys need explicit removal on save; unsaved ad-hoc keys vanish locally.
    if ((meta.keys || []).includes(k)) {
      setRemoved((prev) => (prev.includes(k) ? prev : [...prev, k]));
    }
    setMeta((prev) => ({
      ...prev,
      keys: (prev.keys || []).filter((x) => x !== k),
      credentials: Object.fromEntries(Object.entries(prev.credentials || {}).filter(([x]) => x !== k)),
    }));
  };

  return (
    <div className="skill-creds">
      <div className="skill-creds-header">
        <div className="skill-creds-title-row">
          <h3>Credentials</h3>
          {help && (
            <button
              type="button"
              className="lw-btn lw-btn-ghost lw-help-btn"
              onClick={() => setShowHelp(true)}
              title={help.title}
            >
              <Info size={14} /> Setup guide
            </button>
          )}
        </div>
        <p>
          Saved credentials are passed to the skill as <strong>environment variables</strong> when it runs
          (e.g. <code>os.environ["API_KEY"]</code>). Declare expected keys in this skill's <code>data.json</code>
          (<code>{'"credentials": [{ "name": "API_KEY" }, ...]'}</code>) and they'll show up here automatically. Existing values are masked —
          type a new value to overwrite.
        </p>
      </div>

      {showHelp && help && (
        <div className="modal-overlay" onClick={() => setShowHelp(false)} role="dialog" aria-modal="true">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{help.title}</span>
              <button type="button" className="modal-close" onClick={() => setShowHelp(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modal-body listener-setup-help">{help.body}</div>
          </div>
        </div>
      )}

      {error && <div className="skill-error-banner">{error}</div>}

      {!loading && suggestionGroups.length > 0 && (
        <div className="skill-cred-suggestions">
          {suggestionGroups.map((g) => (
            <div className="skill-cred-suggestion" key={g.slug}>
              <div className="skill-cred-suggestion-text">
                <strong>{g.name}</strong> already has{' '}
                {g.keys.length === 1 ? <code>{g.keys[0]}</code> : `${g.keys.length} matching values`}
                {g.keys.length > 1 && (
                  <span className="skill-cred-suggestion-keys">
                    {' '}({g.keys.join(', ')})
                  </span>
                )}{' '}
                stored. Reuse them here?
              </div>
              <button
                type="button"
                className="lw-btn lw-btn-primary"
                disabled={!!copying}
                onClick={() => handleCopyFrom(g.slug, g.keys)}
              >
                {copying === g.slug ? 'Copying…' : 'Use these values'}
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="skills-loading" style={{ padding: 32 }}>Loading credentials...</div>
      ) : (
        <>
          {allKeys.length === 0 && Object.keys(values).length === 0 ? (
            <div className="skill-empty-creds">
              No credentials defined yet. Add one below or declare them in this skill's <code>data.json</code> under <code>credentials</code>.
            </div>
          ) : (
            <div className="skill-creds-list">
              {allKeys.map((k) => {
                const sensitive = SENSITIVE_RE.test(k);
                const masked = (meta.credentials || {})[k];
                const inputValue = values[k] !== undefined ? values[k] : '';
                const isRevealed = !!revealed[k];
                return (
                  <div className="skill-cred-row" key={k}>
                    <label htmlFor={`cred-${k}`}>{k}</label>
                    <div className="skill-cred-input-row">
                      <input
                        id={`cred-${k}`}
                        className="skill-cred-input"
                        type={sensitive && !isRevealed ? 'password' : 'text'}
                        autoComplete="off"
                        placeholder={masked ? `Saved: ${masked}` : 'Not set'}
                        value={inputValue}
                        onChange={(e) =>
                          setValues((prev) => ({ ...prev, [k]: e.target.value }))
                        }
                      />
                      {sensitive && (
                        <button
                          type="button"
                          className="lw-btn lw-btn-ghost"
                          onClick={() =>
                            setRevealed((prev) => ({ ...prev, [k]: !prev[k] }))
                          }
                          style={{ padding: '6px 10px' }}
                          title={isRevealed ? 'Hide' : 'Show'}
                        >
                          {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}
                      <button
                        type="button"
                        className="lw-btn lw-btn-danger"
                        style={{ padding: '6px 10px' }}
                        title="Remove"
                        onClick={() => handleRemove(k)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="skill-cred-add">
            <input
              className="skill-cred-input"
              type="text"
              placeholder="NEW_KEY"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            <input
              className="skill-cred-input"
              type="text"
              placeholder="value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              autoComplete="off"
            />
            <button
              type="button"
              className="lw-btn lw-btn-ghost"
              onClick={handleAddPair}
              disabled={!newKey.trim()}
            >
              <Plus size={14} /> Add
            </button>
          </div>

          <div className="skill-cred-actions">
            <button
              type="button"
              className="lw-btn lw-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={14} /> {saving ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TerminalPanel({ agentId, skill, isOwned, history, appendHistory, clearHistory }) {
  const initialCommand = useMemo(() => {
    const mf = skill?.manifest || {};
    if (mf.entrypoint) return String(mf.entrypoint);
    return 'python main.py';
  }, [skill?.id, skill?.manifest]);

  const [command, setCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [recallIdx, setRecallIdx] = useState(-1);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setCommand('');
    setError('');
    setRecallIdx(-1);
  }, [skill?.id]);

  // Auto-scroll to bottom whenever history changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history.length, running]);

  const handleRun = async () => {
    const cmd = command.trim();
    if (!cmd || running) return;
    setRunning(true);
    setError('');
    setRecallIdx(-1);
    const startedAt = Date.now();
    try {
      const data = await runAgentSkillCommand(agentId, skill.id, cmd, {
        timeout: 60,
        shell: isOwned,
      });
      appendHistory({
        id: `${startedAt}-${Math.random().toString(36).slice(2, 8)}`,
        ts: startedAt,
        command: cmd,
        status: data?.status || 'success',
        exit_code: data?.exit_code,
        duration_ms: data?.duration_ms,
        stdout: data?.stdout || '',
        stderr: data?.stderr || '',
        error: data?.error || '',
      });
    } catch (e) {
      const msg = e.message || 'Run failed';
      setError(msg);
      appendHistory({
        id: `${startedAt}-${Math.random().toString(36).slice(2, 8)}`,
        ts: startedAt,
        command: cmd,
        status: 'error',
        exit_code: -1,
        duration_ms: 0,
        stdout: '',
        stderr: '',
        error: msg,
      });
    } finally {
      setCommand('');
      setRunning(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRun();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      const next = recallIdx < 0 ? 0 : Math.min(recallIdx + 1, history.length - 1);
      setRecallIdx(next);
      setCommand(history[next]?.command || '');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (recallIdx <= 0) {
        setRecallIdx(-1);
        setCommand('');
      } else {
        const next = recallIdx - 1;
        setRecallIdx(next);
        setCommand(history[next]?.command || '');
      }
      return;
    }
    if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      clearHistory();
    }
  };

  const reversed = useMemo(() => [...history].reverse(), [history]);

  return (
    <div className="skill-term">
      <div className="skill-term-bar">
        <div className="skill-term-bar-left">
          <h3>Terminal</h3>
          <span className="skill-term-cwd">{skill?.storage_path || '/skill'}</span>
        </div>
        <div className="skill-term-bar-right">
          <span className="skill-term-hint">
            ↑/↓ recall · Enter run · {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+L clear
          </span>
          <button
            type="button"
            className="lw-btn lw-btn-ghost"
            onClick={clearHistory}
            disabled={history.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="skill-term-scroll" ref={scrollRef}>
        {reversed.length === 0 ? (
          <div className="skill-term-empty">
            {isOwned
              ? 'Ready. Run any shell command inside this skill folder.'
              : 'Ready. Bundled skills run with the manifest entrypoint as a prefix; no shell metacharacters.'}
            <br />Try: <code>{initialCommand}</code>
          </div>
        ) : (
          reversed.map((entry) => (
            <div className="skill-term-entry" key={entry.id}>
              <div className="skill-term-prompt-line">
                <span className="skill-term-prompt-glyph">$</span>
                <span className="skill-term-prompt-cmd">{entry.command}</span>
                <span className={`skill-term-status skill-term-status-${entry.status || 'unknown'}`}>
                  {entry.status || 'unknown'}
                  {entry.exit_code != null ? ` · exit ${entry.exit_code}` : ''}
                  {entry.duration_ms != null ? ` · ${entry.duration_ms}ms` : ''}
                </span>
              </div>
              {entry.error && <div className="skill-term-error">{entry.error}</div>}
              {entry.stdout && <pre className="skill-term-stdout">{entry.stdout}</pre>}
              {entry.stderr && <pre className="skill-term-stderr">{entry.stderr}</pre>}
            </div>
          ))
        )}
        {running && (
          <div className="skill-term-entry">
            <div className="skill-term-prompt-line">
              <span className="skill-term-prompt-glyph">$</span>
              <span className="skill-term-prompt-cmd">{/* trailing live command shown in input */}</span>
              <span className="skill-term-status skill-term-status-running">running…</span>
            </div>
          </div>
        )}
      </div>

      <form
        className="skill-term-input-row"
        onSubmit={(e) => { e.preventDefault(); handleRun(); }}
      >
        <span className="skill-term-prompt-glyph">$</span>
        <input
          ref={inputRef}
          className="skill-term-input"
          type="text"
          value={command}
          onChange={(e) => { setCommand(e.target.value); setRecallIdx(-1); }}
          onKeyDown={handleKeyDown}
          placeholder={running ? 'Running…' : initialCommand}
          spellCheck={false}
          autoComplete="off"
          disabled={running}
          autoFocus
        />
      </form>

      {error && <div className="skill-error-banner" style={{ margin: '8px 12px 0' }}>{error}</div>}
    </div>
  );
}

function LogsPanel({ agentId, skillId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [clearing, setClearing] = useState(false);

  const reload = useCallback(async () => {
    if (!agentId || !skillId) return;
    setLoading(true);
    setError('');
    try {
      const rows = await fetchAgentSkillRuns(agentId, skillId, { limit: 100 });
      setRuns(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e?.message || 'Failed to load run history');
    } finally {
      setLoading(false);
    }
  }, [agentId, skillId]);

  useEffect(() => {
    setOpenIds(new Set());
    reload();
  }, [reload]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleClear = async () => {
    if (!window.confirm('Delete all recorded agent runs of this skill? This cannot be undone.')) return;
    setClearing(true);
    try {
      await clearAgentSkillRuns(agentId, skillId);
      setRuns([]);
      setOpenIds(new Set());
    } catch (e) {
      setError(e?.message || 'Failed to clear run history');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="skill-logs-empty">
        <h3>Agent run history</h3>
        <p>Loading recent agent runs…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skill-logs-empty">
        <h3>Agent run history</h3>
        <p style={{ color: 'var(--danger, #ff6b6b)' }}>{error}</p>
        <button type="button" className="lw-btn lw-btn-ghost" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="skill-logs-empty">
        <h3>Agent run history</h3>
        <p>
          Nothing here yet. This tab shows every time the agent itself runs this skill
          during a real conversation — manual Terminal tests stay in the Terminal tab and
          do not appear here.
        </p>
        <button type="button" className="lw-btn lw-btn-ghost" onClick={reload}>
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="skill-logs">
      <div className="skill-logs-bar">
        <h3>Agent run history</h3>
        <span className="skill-logs-meta">
          {runs.length} agent run{runs.length === 1 ? '' : 's'} · stored on the server
        </span>
        <button
          type="button"
          className="lw-btn lw-btn-ghost"
          onClick={reload}
          style={{ marginLeft: 'auto' }}
        >
          Refresh
        </button>
        <button
          type="button"
          className="lw-btn lw-btn-ghost"
          onClick={handleClear}
          disabled={clearing}
        >
          {clearing ? 'Clearing…' : 'Clear all'}
        </button>
      </div>

      <div className="skill-logs-list">
        {runs.map((entry) => {
          const open = openIds.has(entry.id);
          const when = entry.created_at ? new Date(entry.created_at).toLocaleString() : '';
          return (
            <div className="skill-log-row" key={entry.id}>
              <button
                type="button"
                className="skill-log-head"
                onClick={() => toggle(entry.id)}
              >
                <span className="skill-log-chevron">
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span className={`skill-term-status skill-term-status-${entry.status || 'unknown'}`}>
                  {entry.status || '?'}
                </span>
                <code className="skill-log-cmd">{entry.command || '(no command)'}</code>
                <span className="skill-log-when">{when}</span>
                <span className="skill-log-meta">
                  {entry.action_type ? `${entry.action_type} · ` : ''}
                  exit {entry.exit_code != null ? entry.exit_code : '?'}
                  {entry.duration_ms != null ? ` · ${entry.duration_ms}ms` : ''}
                  {entry.event_source ? ` · ${entry.event_source}` : ''}
                </span>
              </button>
              {open && (
                <div className="skill-log-body">
                  <div className="skill-log-streams">
                    <div>
                      <div className="skill-log-stream-label">stdout</div>
                      <pre className="skill-term-stdout">{entry.stdout || '(empty)'}</pre>
                    </div>
                    <div>
                      <div className="skill-log-stream-label">stderr</div>
                      <pre className="skill-term-stderr">{entry.stderr || '(empty)'}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilesPanel({ agentId, skill, isOwned, refreshSkill }) {
  const files = useMemo(() => skill?.files || [], [skill]);
  const [activePath, setActivePath] = useState(() => pickInitialFile(files));
  const [content, setContent] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const folderInputRef = useRef(null);
  const lastLoadedRef = useRef('');

  useEffect(() => {
    setActivePath((prev) => {
      if (prev && files.includes(prev)) return prev;
      return pickInitialFile(files);
    });
  }, [skill?.id, files]);

  useEffect(() => {
    let cancelled = false;
    if (!activePath) {
      setContent('');
      setLoaded(false);
      return undefined;
    }
    setLoaded(false);
    setDirty(false);
    setError('');
    (async () => {
      try {
        const data = await readAgentSkillFile(agentId, skill.id, activePath);
        if (cancelled) return;
        const text = data?.content ?? '';
        setContent(text);
        lastLoadedRef.current = text;
        setLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load file');
          setContent('');
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, skill?.id, activePath]);

  const handleChange = (value) => {
    if (!isOwned) return;
    const next = value ?? '';
    setContent(next);
    setDirty(next !== lastLoadedRef.current);
  };

  const handleSave = async () => {
    if (!activePath || !isOwned || !dirty) return;
    setSaving(true);
    setError('');
    try {
      await writeAgentSkillFile(agentId, skill.id, activePath, content);
      lastLoadedRef.current = content;
      setDirty(false);
    } catch (e) {
      setError(e.message || 'Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFile = () => {
    if (!isOwned) return;
    setShowAddModal(true);
  };

  const submitAddFile = async ({ path, content: bodyContent }) => {
    const cleanPath = String(path || '').trim().replace(/^\/+/, '');
    if (!cleanPath || cleanPath.includes('..')) {
      throw new Error('Invalid path');
    }
    if (files.includes(cleanPath)) {
      throw new Error(`"${cleanPath}" already exists. Pick a different name.`);
    }
    setAdding(true);
    setError('');
    try {
      await writeAgentSkillFile(agentId, skill.id, cleanPath, bodyContent ?? '');
      await refreshSkill();
      setActivePath(cleanPath);
      setShowAddModal(false);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!isOwned || !activePath) return;
    if (PROTECTED_SKILL_FILES.has(activePath)) {
      setError(`${activePath} cannot be deleted.`);
      return;
    }
    if (!window.confirm(`Delete "${activePath}"? This is permanent.`)) return;
    setDeleting(true);
    setError('');
    try {
      await deleteAgentSkillFile(agentId, skill.id, activePath);
      await refreshSkill();
      setActivePath('');
    } catch (e) {
      setError(e.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    setError('');
    try {
      await downloadAgentSkillZip(agentId, skill.id);
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadFolder = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (folderInputRef.current) folderInputRef.current.value = '';
    if (!picked.length) return;
    if (!isOwned) {
      setError('This skill is bundled — uploads are disabled.');
      return;
    }
    const overwrites = picked
      .map((f) => (f.webkitRelativePath || f.name).split('/').slice(1).join('/') || f.name)
      .filter((rel) => files.includes(rel));
    if (overwrites.length && !window.confirm(
      `Upload will overwrite ${overwrites.length} existing file(s):\n` +
      overwrites.slice(0, 6).join('\n') +
      (overwrites.length > 6 ? `\n…and ${overwrites.length - 6} more` : '') +
      '\n\nContinue?'
    )) return;
    setUploading(true);
    setError('');
    try {
      const res = await uploadAgentSkillFolder(agentId, skill.id, picked, { stripTop: true });
      await refreshSkill();
      if (res?.written?.length && (!activePath || !files.includes(activePath))) {
        setActivePath(res.written[0]);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="skill-editor">
      <div className="skill-editor-tree">
        <div className="skill-editor-tree-bar">
          {isOwned && (
            <button
              type="button"
              className="lw-btn lw-btn-ghost"
              onClick={handleAddFile}
              disabled={adding}
              title="Add a new file to this skill"
            >
              <FilePlus size={13} /> {adding ? 'Adding…' : 'New File'}
            </button>
          )}
          <button
            type="button"
            className="lw-btn lw-btn-ghost"
            onClick={handleDownloadZip}
            disabled={downloading || !files.length}
            title="Download this skill as a .zip"
          >
            <Download size={13} /> {downloading ? '…' : 'Zip'}
          </button>
          {isOwned && (
            <>
              <button
                type="button"
                className="lw-btn lw-btn-ghost"
                onClick={() => folderInputRef.current?.click()}
                disabled={uploading}
                title="Upload a folder of files (overwrites by path)"
              >
                <FolderUp size={13} /> {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <input
                ref={folderInputRef}
                type="file"
                multiple
                webkitdirectory=""
                directory=""
                style={{ display: 'none' }}
                onChange={handleUploadFolder}
              />
            </>
          )}
        </div>
        <SkillFileTree
          files={files}
          activePath={activePath}
          onPick={setActivePath}
        />
      </div>
      <div className="skill-editor-pane">
        <div className="skill-editor-bar">
          <div className="skill-editor-path">
            {activePath || '— no file selected —'}
          </div>
          <div className="skill-editor-actions">
            {isOwned && activePath && !PROTECTED_SKILL_FILES.has(activePath) && (
              <button
                type="button"
                className="lw-btn lw-btn-danger"
                style={{ padding: '6px 10px' }}
                onClick={handleDeleteFile}
                disabled={deleting}
                title="Delete this file"
              >
                <Trash2 size={13} />
              </button>
            )}
            {isOwned && (
              <button
                type="button"
                className="lw-btn lw-btn-primary"
                style={{ padding: '6px 12px' }}
                onClick={handleSave}
                disabled={!dirty || saving}
              >
                <Save size={13} /> {saving ? 'Saving...' : dirty ? 'Save' : 'Saved'}
              </button>
            )}
          </div>
        </div>
        {error && (
          <div className="skill-error-banner" style={{ margin: '8px 12px 0' }}>
            {error}
          </div>
        )}
        {!activePath ? (
          <div className="skill-editor-empty">Select a file to view or edit.</div>
        ) : !loaded ? (
          <div className="skill-editor-empty">Loading...</div>
        ) : (
          <div className="skill-editor-monaco">
            <Editor
              height="100%"
              theme="vs-dark"
              language={languageForPath(activePath)}
              value={content}
              onChange={handleChange}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: "'SF Mono', 'Monaco', 'Inter', monospace",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: 'on',
                tabSize: 2,
                renderLineHighlight: 'gutter',
              }}
            />
          </div>
        )}
      </div>
      {showAddModal && (
        <NewFileModal
          existingFiles={files}
          onClose={() => setShowAddModal(false)}
          onSubmit={submitAddFile}
          submitting={adding}
        />
      )}
    </div>
  );
}

function NewFileModal({ existingFiles, onClose, onSubmit, submitting }) {
  const [path, setPath] = useState('');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handlePickFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = typeof e.target?.result === 'string' ? e.target.result : '';
      setContent(text);
      setFileName(file.name);
      if (!path.trim()) setPath(file.name);
      setError('');
    };
    reader.onerror = () => setError('Could not read file');
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    try {
      await onSubmit({ path, content });
    } catch (err) {
      setError(err?.message || 'Failed to create file');
    }
  };

  const clearUpload = () => {
    setContent('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return createPortal(
    <div className="sw-publish-overlay" onClick={onClose}>
      <div className="sw-publish-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sw-publish-header">
          <h2>New file</h2>
          <button className="sw-publish-close" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <form className="sw-publish-body" onSubmit={handleSubmit}>
          <p className="sw-publish-blurb">
            Add a file to this skill — type a path or upload one from your computer.
            Examples: <code>helper.py</code>, <code>lib/utils.py</code>, <code>README.md</code>.
          </p>

          <div className="form-group">
            <label>File path (relative to the skill folder)</label>
            <input
              autoFocus
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="helper.py"
              disabled={submitting}
            />
          </div>

          <div className="sw-newfile-upload">
            <label className="sw-newfile-upload-label">Or upload a file</label>
            <input
              ref={fileInputRef}
              type="file"
              id="sw-newfile-input"
              className="sw-newfile-file-input"
              onChange={(e) => handlePickFile(e.target.files?.[0])}
              disabled={submitting}
            />
            {fileName ? (
              <div className="sw-newfile-uploaded">
                <span className="sw-newfile-uploaded-name" title={fileName}>
                  <Upload size={12} /> {fileName}
                </span>
                <button
                  type="button"
                  className="sw-newfile-uploaded-clear"
                  onClick={clearUpload}
                  disabled={submitting}
                >
                  Remove
                </button>
              </div>
            ) : (
              <label htmlFor="sw-newfile-input" className="sw-newfile-dropzone">
                <Upload size={16} />
                <span>Choose a file…</span>
                <span className="sw-newfile-dropzone-hint">Text, code, or markdown</span>
              </label>
            )}
          </div>

          {error && <div className="sw-publish-error">{error}</div>}

          <div className="sw-publish-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !path.trim()}
            >
              {submitting ? <Loader2 size={14} className="spin" /> : null}
              {submitting ? 'Adding…' : 'Add file'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}


export default function SkillWorkspace() {
  const { agentId, skillId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uninstalling, setUninstalling] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [history, setHistory] = useState([]);


  const refreshSkill = useCallback(async () => {
    const data = await getAgentSkill(agentId, skillId);
    setSkill(data);
    return data;
  }, [agentId, skillId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getAgentSkill(agentId, skillId);
        if (!cancelled) setSkill(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load skill');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, skillId]);

  // Load run history from localStorage when target skill changes.
  useEffect(() => {
    setHistory(loadHistory(agentId, skillId));
  }, [agentId, skillId]);

  const appendHistory = useCallback((entry) => {
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, HISTORY_LIMIT);
      saveHistory(agentId, skillId, next);
      return next;
    });
  }, [agentId, skillId]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    saveHistory(agentId, skillId, []);
  }, [agentId, skillId]);

  const isOwned = skill ? (skill.agent_id === agentId || skill.agent_id == null) : false;
  const isEnabled = skill?.enabled !== false;

  const goBack = () => navigate(`/portal/agent/${agentId}/connections`);

  const handleToggleEnabled = async () => {
    if (!skill) return;
    setToggling(true);
    setError('');
    try {
      if (isEnabled) {
        await disableAgentSkill(agentId, skillId);
      } else {
        await enableAgentSkill(agentId, skillId);
      }
      await refreshSkill();
    } catch (e) {
      setError(e.message || 'Failed to toggle skill');
    } finally {
      setToggling(false);
    }
  };

  const handleUninstall = async () => {
    if (!isOwned) return;
    if (!window.confirm(`Uninstall "${skill.name || skill.id}"? This deletes the skill folder and removes it from this agent.`)) return;
    setUninstalling(true);
    try {
      await uninstallAgentSkill(agentId, skillId);
      goBack();
    } catch (e) {
      setError(e.message || 'Uninstall failed');
      setUninstalling(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'files', label: 'Files' },
    { id: 'credentials', label: 'Credentials' },
    { id: 'terminal', label: 'Terminal' },
    { id: 'logs', label: 'Logs' },
  ];

  // The agent reaches this skill as a smolagents Tool: the skill folder is
  // auto-wrapped and the model calls it as a Python function. Derive the same
  // signature the backend builds so the Overview shows exactly how it's invoked.
  const toolSignature = useMemo(() => {
    if (!skill) return null;
    const rawSlug = skill.slug || skill.name || skill.id || 'skill';
    let name = String(rawSlug).trim().replace(/\W/g, '_') || 'skill';
    if (/^\d/.test(name)) name = `s_${name}`;
    const mf = skill.manifest || {};
    const entrypoint = String(mf.entrypoint || 'python main.py').trim();
    const examples = Array.isArray(mf.examples) ? mf.examples : [];
    const signature = skill.signature || `${name}(args: string) -> string`;
    const inputs = skill.inputs && typeof skill.inputs === 'object' ? skill.inputs : null;
    return { name, entrypoint, examples, signature, inputs };
  }, [skill]);

  return (
    <div className="sw skill-workspace">
      <div className="skill-ws-topbar">
        <button type="button" className="skill-ws-back" onClick={goBack}>
          <ArrowLeft size={14} /> Back to Connections
        </button>
        {loading && <span className="skill-ws-status">Loading...</span>}
        {skill && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="lw-btn lw-btn-ghost"
              onClick={handleToggleEnabled}
              disabled={toggling}
              title={isEnabled ? 'Disable for this agent' : 'Enable for this agent'}
            >
              {isEnabled ? <PowerOff size={13} /> : <Power size={13} />}
              {toggling ? '...' : isEnabled ? 'Disable' : 'Enable'}
            </button>
            {isOwned && (
              <button
                type="button"
                className="lw-btn lw-btn-danger"
                onClick={handleUninstall}
                disabled={uninstalling}
              >
                <Trash2 size={13} /> {uninstalling ? 'Uninstalling...' : 'Uninstall'}
              </button>
            )}
          </div>
        )}
      </div>


      <div className="skill-ws-hero">
        <div className="skill-ws-hero-left">
          <span className="skill-ws-hero-icon"><Sparkles size={20} /></span>
          <div style={{ minWidth: 0 }}>
            <h2>{skill?.name || skillId}</h2>
            <div className="skill-ws-hero-meta">
              <code>{skill?.id || skillId}</code>
              <span>v{skill?.version || '0.0.1'}</span>
              <span>{isOwned ? `${skill?.source || 'agent'} skill` : 'Bundled'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="skill-ws-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`skill-ws-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="skill-error-banner">{error}</div>}

      <div className="skill-ws-body">
        {activeTab === 'overview' && (
          <div className="skill-overview">
            {toolSignature && (
              <div className="skill-signature">
                <div className="skill-signature-head">
                  <span className="skill-signature-title">Tool signature</span>
                  <span className="skill-signature-sub">
                    How the agent calls this skill
                  </span>
                </div>
                <pre className="skill-signature-code">
                  <code>{`def ${toolSignature.signature}`}</code>
                </pre>
                {toolSignature.inputs && Object.keys(toolSignature.inputs).length > 0 && (
                  <div className="skill-signature-inputs">
                    <span className="skill-signature-label">Inputs</span>
                    <ul>
                      {Object.entries(toolSignature.inputs).map(([key, spec]) => (
                        <li key={key}>
                          <code>{key}</code>
                          <span className="skill-signature-type">
                            {((spec && spec.type) || 'string') + (spec && spec.nullable ? '?' : '')}
                          </span>
                          {spec && spec.description ? (
                            <span className="skill-signature-desc"> — {spec.description}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="skill-signature-note">
                  The agent calls this skill as a typed smolagents tool. It runs in an
                  isolated subprocess inside this skill folder, with its credentials in
                  the environment, and the return value is handed back to the agent.
                </p>
                {toolSignature.examples.length > 0 && (
                  <div className="skill-signature-examples">
                    <span className="skill-signature-label">Examples</span>
                    <ul>
                      {toolSignature.examples.map((ex, i) => (
                        <li key={i}>
                          <code>{typeof ex === 'string' ? ex : JSON.stringify(ex)}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="skill-overview-md">
              {(skill?.description_md || skill?.body) ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {skill.description_md || skill.body}
                </ReactMarkdown>
              ) : (
                <p style={{ color: 'var(--text-muted)' }}>
                  {loading ? 'Loading description...' : 'No description.md yet. Add one from the Files tab — it is what the agent reads to learn this tool.'}
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'files' && skill && (
          <FilesPanel
            agentId={agentId}
            skill={skill}
            isOwned={isOwned}
            refreshSkill={refreshSkill}
          />
        )}

        {activeTab === 'credentials' && skill && (
          <CredentialsPanel agentId={agentId} skill={skill} />
        )}

        {activeTab === 'terminal' && skill && (
          <TerminalPanel
            agentId={agentId}
            skill={skill}
            isOwned={isOwned}
            history={history}
            appendHistory={appendHistory}
            clearHistory={clearHistory}
          />
        )}

        {activeTab === 'logs' && skill && (
          <LogsPanel agentId={agentId} skillId={skillId} />
        )}
      </div>
    </div>
  );
}
